import { createElement } from 'react';
import {
  coreExtensionData,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import { ServicesSubPage } from './ServicesPage/ServicesSubPage';
import { SubPageRoutes } from './ServicesPage/SubPageRoutes';
import { DepartmentPickerFieldExtension } from './DepartmentPicker';
import { DivisionPickerFieldExtension } from './DivisionPicker';
import { OwnerEmailPickerFieldExtension } from './OwnerEmailPicker';
import { DataOfficerFieldExtension } from './DataOfficerField';
import { ParticipationAgreementFieldExtension } from './ParticipationAgreementField';
import { AddUsersFieldExtension } from './AddUsersField';
import { SpokeAdminsFieldExtension } from './SpokeAdminsField';
import { ExternalDataLoadingRequiredFieldExtension } from './ExternalDataLoadingRequiredField';
import { AdoProjectNameFieldExtension } from './AdoProjectNameField';
import { DataTeamSetupFieldExtension } from './DataTeamSetupField';
import { RequestedByFieldExtension } from './RequestedByField';
import { RegionPickerFieldExtension } from './RegionPicker';
import { SpokeNameFieldExtension } from './SpokeNameField';
import { DatabricksWorkspaceFieldExtension } from './DatabricksWorkspaceField';
import {
  CatalogsAndSchemasFieldExtension,
  CatalogConflictGuardFieldExtension,
} from './CatalogsAndSchemasField';
import { ReviewSummaryFieldExtension } from './ReviewSummaryField';
import { ExistingCatalogSchemasFieldExtension } from './ExistingCatalogSchemasField/extensions';
import { UserGroupsFieldExtension } from './UserGroupsField';
import { UserGroupMembersFieldExtension } from './UserGroupMembersField';

export const scaffolderCustomizations = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [
    // "Templates" and "Create" are Backstage terms; users know these as services.
    // The page also loses its tab row: the only other tab, Tasks, just sent the
    // user back here, and a second row of navigation competes with the sidebar.
    // Sub-pages stay attached, so their routes (task progress) still resolve.
    scaffolderPlugin.getExtension('page:scaffolder').override({
      factory(originalFactory, { inputs }) {
        const pages = inputs.pages.map(page => ({
          path: page.get(coreExtensionData.routePath),
          element: page.get(coreExtensionData.reactElement),
        }));
        return originalFactory({
          params: {
            title: 'Services',
            loader: async () => createElement(SubPageRoutes, { pages }),
          },
        });
      },
    }),
    // Swap the stock template list (search and filter column, owner and type
    // on each card) for our grouped Services list. The stock element still
    // serves the template wizard below it.
    scaffolderPlugin.getExtension('sub-page:scaffolder/templates').override({
      *factory(originalFactory) {
        const original = originalFactory({ params: { title: 'Services' } });
        yield* original;
        yield coreExtensionData.reactElement(
          createElement(ServicesSubPage, {
            wizard: original.get(coreExtensionData.reactElement),
          }),
        );
      },
    }),
    // Override the tasks sub-page so logs are collapsed by default on the task
    // detail page. The task list route redirects to /create.
    scaffolderPlugin.getExtension('sub-page:scaffolder/tasks').override({
      params: {
        loader: async () => {
          const { CustomTasksSubPage } = await import('./CustomOngoingTask');
          return createElement(CustomTasksSubPage);
        },
      },
    }),
    scaffolderPlugin
      .getExtension('sub-page:scaffolder/actions')
      .override({ disabled: true }),
    scaffolderPlugin
      .getExtension('sub-page:scaffolder/editor')
      .override({ disabled: true }),
    scaffolderPlugin
      .getExtension('sub-page:scaffolder/templating-extensions')
      .override({ disabled: true }),
    DepartmentPickerFieldExtension,
    DivisionPickerFieldExtension,
    OwnerEmailPickerFieldExtension,
    DataOfficerFieldExtension,
    ParticipationAgreementFieldExtension,
    AddUsersFieldExtension,
    SpokeAdminsFieldExtension,
    ExternalDataLoadingRequiredFieldExtension,
    AdoProjectNameFieldExtension,
    DataTeamSetupFieldExtension,
    RequestedByFieldExtension,
    RegionPickerFieldExtension,
    SpokeNameFieldExtension,
    DatabricksWorkspaceFieldExtension,
    CatalogsAndSchemasFieldExtension,
    CatalogConflictGuardFieldExtension,
    ReviewSummaryFieldExtension,
    ExistingCatalogSchemasFieldExtension,
    UserGroupsFieldExtension,
    UserGroupMembersFieldExtension,
  ],
});
